import { useCallback } from "react";
import styled from "styled-components";
import { motion } from "../../AuthNew/shared/motionUtils";
import {
  ModalTitle,
  ButtonGroup,
  slideInRight,
} from "../shared/onboardingStyles";
import {
  PrimaryButton,
  BackButton,
} from "../../AuthNew/shared/authStyles";
import { Icon, icons } from "../../../components/Icon";
import { TickCircleFilledIcon } from "../../AuthNew/shared/icons/AuthIcons";

// Progress Bar (Rectangle 5) - matches Step1Organization
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// Main Content Container - horizontal layout for cards
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 534px;
`;

// Options Container - horizontal layout
const OptionsContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: stretch;
`;

// Option Card - styled to match Figma design exactly
const OptionCard = styled(motion.button)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? "#2266ef" : "#cccfd4")};
  border-radius: 8px;
  background: white;
  cursor: pointer;
  flex: 1;
  text-align: left;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: ${({ isSelected }) =>
    isSelected
      ? "0px 4px 25px 0px rgba(34, 102, 239, 0.15)"
      : "none"};

  &:hover {
    border-color: ${({ isSelected }) => (isSelected ? "#2266ef" : "#999faa")};
  }
`;

const CheckIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

// Empty circle for unselected state
const EmptyCircle = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid #cccfd4;
  border-radius: 50%;
  background: transparent;
  flex-shrink: 0;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const OptionTitle = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0;
`;

const OptionDescription = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #353a46;
  margin: 0;
`;

const Step2OrganizationType = ({
  data,
  onChange,
  onNext,
  onBack,
  progress = 33.33, // Default progress for step 2 of 6
}) => {
  // Two options exactly as per Figma design
  const options = [
    {
      value: "operation",
      title: "Employees and Operations",
      description:
        "Best for operations-heavy organizations that focus on internal workflows and don't require customer management.",
    },
    {
      value: "service",
      title: "Employees and Customers",
      description:
        "Best for service-based businesses that handle customer jobs, appointments, and service requests.",
    },
  ];

  const selectedValue = data.organizationType || null;

  const handleSelect = useCallback(
    value => {
      onChange({ organizationType: value });
    },
    [onChange]
  );

  const handleNext = useCallback(() => {
    if (selectedValue) {
      onNext();
    }
  }, [selectedValue, onNext]);

  const isNextDisabled = !selectedValue;

  return (
    <motion.div
      variants={slideInRight}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Progress Bar (Rectangle 5) */}
      <ProgressBarContainer>
        <ProgressBarFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </ProgressBarContainer>

      {/* Title */}
      <div style={{ marginTop: "24px" }}>
        <ModalTitle>I will use FieldServicely to manage</ModalTitle>
      </div>

      {/* Selection Options - Horizontal Layout */}
      <ContentWrapper style={{ marginTop: "24px" }}>
        <OptionsContainer>
          {options.map(option => {
            const isSelected = selectedValue === option.value;

            return (
              <OptionCard
                key={option.value}
                type="button"
                isSelected={isSelected}
                onClick={() => handleSelect(option.value)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <CheckIconWrapper>
                  {isSelected ? (
                    <TickCircleFilledIcon 
                      size="20px" 
                      color="#2266ef"
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  ) : (
                    <EmptyCircle />
                  )}
                </CheckIconWrapper>
                <OptionContent>
                  <OptionTitle>{option.title}</OptionTitle>
                  <OptionDescription>{option.description}</OptionDescription>
                </OptionContent>
              </OptionCard>
            );
          })}
        </OptionsContainer>
      </ContentWrapper>

      {/* Back and Next Buttons */}
      <ButtonGroup marginTop="32px" style={{ maxWidth: "534px" }}>
        <BackButton
          type="button"
          onClick={onBack}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          height="40px"
          style={{
            background: "#e9f0fe",
            borderRadius: "32px",
            padding: "8px 16px",
            width: "100px",
            fontSize: "14px",
            fontWeight: 700,
            color: "#000e2a",
            border: "none",
          }}
        >
          <Icon component={icons.back} />
          Back
        </BackButton>
        <PrimaryButton
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          height="40px"
          fontSize="16px"
          style={{
            flex: 1,
            borderRadius: "32px",
            padding: "8px 16px",
            background:
              "linear-gradient(to right, #2266ef 0%, #6c2ef2 100%)",
            color: "white",
            fontWeight: 700,
          }}
        >
          Next
        </PrimaryButton>
      </ButtonGroup>
    </motion.div>
  );
};

export default Step2OrganizationType;
